// components/AppRouter.tsx

import React, { Suspense, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Header from "./Header";
import { resolveLandingRoute } from "@/lib/auth/landingRoute";
import { supabase } from "../lib/supabaseClient";
// Error pages - eagerly loaded for immediate availability
import NotFoundPage from "./errors/NotFoundPage";
import ServerErrorPage from "./errors/ServerErrorPage";
import NetworkErrorPage from "./errors/NetworkErrorPage";
// IMPORTANT: Keep only the smallest shell eagerly loaded; large feature routes split.
// Use explicit webpackChunkName-like comments for more predictable chunk names in Vite/Rollup.
const PlanningContainer = React.lazy(
  () => import(/* planning-chunk */ "./planning/PlanningContainer")
);
const WeekMatchesPage = React.lazy(
  () => import(/* planning-chunk */ "./planning/WeekMatchesPage")
);
const UrgentsPage = React.lazy(
  () => import(/* planning-chunk */ "./planning/UrgentsPage")
);
const ToPlanPage = React.lazy(
  () => import(/* planning-chunk */ "./planning/ToPlanPage")
);
const AllMatchesPage = React.lazy(
  () => import(/* planning-chunk */ "./planning/AllMatchesPage")
);
const CalendarPage = React.lazy(() => import("./planning/CalendarPage"));
const GameDayFocusPage = React.lazy(() => import("./planning/GameDayFocusPage"));
const FafAuditPage = React.lazy(() => import("./planning/FafAuditPage"));
const EngagementsPage = React.lazy(() => import("./planning/EngagementsPage"));

const CalendarContainer = React.lazy(() => import("./calendar/CalendarContainer"));
const CalendarLibraryPage = React.lazy(() => import("./calendar/CalendarLibraryPage"));
const CandidatesHubPage = React.lazy(() => import("./calendar/hub/CandidatesHubPage"));
const ModePickerPage = React.lazy(() => import("./calendar/generate/ModePickerPage"));
const OptimisePage = React.lazy(() => import("./calendar/generate/OptimisePage"));
const ManualPage = React.lazy(() => import("./calendar/generate/ManualPage"));
const CandidateDetailPage = React.lazy(() => import("./calendar/candidate/CandidateDetailPage"));
const CalendarConfigPage = React.lazy(() => import("./calendar/CalendarConfigPage"));
const CalendarDrawRoute = React.lazy(() => import("./calendar/draw/CalendarDrawRoute"));

const OfficialsManagementContainer = React.lazy(
  () =>
    import(
      /* officials-management-chunk */ "./containers/OfficialsManagementContainer"
    )
);
const RefereeProposalsPage = React.lazy(() => import("./refereeProposals/RefereeProposalsPage"));
const FinancesContainer = React.lazy(
  () => import(/* finances-chunk */ "./containers/FinancesContainer")
);
const ClubsContainer = React.lazy(
  () => import(/* clubs-chunk */ "./containers/ClubsContainer")
);
const StadiumsContainer = React.lazy(
  () => import(/* stadiums-chunk */ "./containers/StadiumsContainer")
);
const SettingsContainer = React.lazy(
  () => import(/* settings-chunk */ "./settings/SettingsContainer")
);
const AccountingContainer = React.lazy(
  () => import(/* accounting-chunk */ "./containers/AccountingContainer")
);
const VirementsContainer = React.lazy(
  () => import(/* virements-chunk */ "./containers/VirementsContainer")
);
const EtatsContainer = React.lazy(
  () => import(/* etats-chunk */ "./containers/EtatsContainer")
);
const StadiumAuditPlannerContainer = React.lazy(
  () => import(/* missions-chunk */ "./containers/StadiumAuditPlannerContainer")
);
const DisciplinaryContainer = React.lazy(
  () => import(/* disciplinary-chunk */ "./containers/DisciplinaryContainer")
);
const RankingsContainer = React.lazy(
  () => import(/* rankings-chunk */ "./containers/RankingsContainer")
);
const AuditLogContainer = React.lazy(
  () => import(/* audit-chunk */ "./containers/AuditLogContainer")
);
const ManagementContainer = React.lazy(
  () => import(/* management-chunk */ "./containers/ManagementContainer")
);
// News management
const NewsContainer = React.lazy(
  () => import(/* news-chunk */ "./containers/NewsContainer")
);
// Media Accreditation container (unified with internal tabs)
const MediaContainer = React.lazy(
  () => import(/* media-chunk */ "./containers/MediaContainer")
);
// Gate scan mode (full screen, phone camera) — its own chunk, and the QR
// decoder fallback (jsqr) is a further on-demand chunk inside it.
const MediaScanModePage = React.lazy(
  () => import("./media/MediaScanModePage")
);
import { useAuth } from "../contexts/AuthContext";
import { useAppSettings } from "../hooks/useAppSettings";
import { useLeagues } from "../hooks/useLeagues";
import { useSeasons } from "../hooks/useSeasonTransition";
import { useScopeFromUrl } from "@/hooks/useScopeFromUrl";
// Removed unused imports that were causing unnecessary data fetching:
// - useLeagueGroups (now loaded in RankingsView)
// - useTeams (now loaded in RankingsView)
// - useMatches (now loaded in RankingsView)
// - useAuditLogs (now loaded in AuditLogView)
import { UserRole } from "../types";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";
import { EXTERNAL_LEAGUE_CODE } from "@/constants/leagues";
import { useLeagueIdsWithGroups } from "@/hooks/useLeagueIdsWithGroups";
import {
  getHeaderLeagueOptions,
  getUsableLeagues,
  pickDefaultLeagueId,
} from "@/lib/leagues/leagueSelection";
import FullScreenLoader from "./ui/FullScreenLoader";
import RouteFallbackSkeleton from "./ui/skeletons/RouteFallbackSkeleton";
import { useLeagueModules } from "@/hooks/useLeagueModules";
import type { ModuleKey } from "@/lib/modules/registry";
import { useMaintenanceStatus } from "@/hooks/useMaintenanceStatus";
import { isSuperAdmin as checkIsSuperAdmin } from "@/lib/auth/isSuperAdmin";
import { isMaintenanceBlocking } from "@/utils/maintenance";
import MaintenanceScreen from "./maintenance/MaintenanceScreen";
import MaintenanceBanner from "./maintenance/MaintenanceBanner";
import { canOpenSettings } from "@/lib/permissions/settingsAccess";
/** Permission-derived landing page. See lib/auth/landingRoute.ts for why. */
const useLandingRoute = (): string => {
  const { user, permissions } = useAuth();
  const { modules } = useLeagueModules();
  return resolveLandingRoute(user, permissions, modules);
};

const ProtectedRoute = ({
  isAllowed,
  moduleKey,
  featureKey,
  children,
}: {
  isAllowed: boolean;
  moduleKey?: ModuleKey;
  featureKey?: string;
  children: React.ReactNode;
}) => {
  const landing = useLandingRoute();
  const location = useLocation();
  const { modules } = useLeagueModules();

  const moduleAllowed =
    !moduleKey ||
    (featureKey
      ? modules.isFeatureEnabled(moduleKey, featureKey)
      : modules.isModuleEnabled(moduleKey));

  if (!isAllowed || !moduleAllowed) {
    // Guard against the degenerate case where the landing page is the very page
    // being refused (a user with `view dashboard` denied on a /planning child).
    if (location.pathname === landing) {
      return <Navigate to="/404" replace />;
    }
    return <Navigate to={landing} replace />;
  }
  return <>{children}</>;
};

// Component to handle default redirects based on user role
const DefaultRedirect: React.FC = () => {
  return <Navigate to={useLandingRoute()} replace />;
};

/**
 * `/planning` index redirect. It cannot hardcode `week`: with `planning` enabled
 * but its `week` feature disabled, the child gate would refuse /planning/week and
 * send the user to the landing route — which for those roles is /planning itself,
 * back to the index, back to week. Redirecting to the first ENABLED tab breaks
 * that loop; `/404` is the un-gated dead end when every tab is off.
 * `game_day_focus` is deliberately absent: its route needs params.
 */
const PLANNING_INDEX_TABS: ReadonlyArray<readonly [feature: string, path: string]> = [
  ["week", "week"],
  ["urgents", "urgents"],
  ["to_plan", "to-plan"],
  ["all_matches", "all"],
  ["calendar_views", "calendar"],
  ["faf_audit", "faf-audit"],
  ["engagements", "engagements"],
];

const PlanningIndexRedirect: React.FC = () => {
  const { modules } = useLeagueModules();
  const first = PLANNING_INDEX_TABS.find(([feature]) =>
    modules.isFeatureEnabled("planning", feature)
  );
  return <Navigate to={first ? first[1] : "/404"} replace />;
};

// `useScopeFromUrl` a besoin du contexte du routeur, que AppRouter crée lui-même.
const ScopeFromUrl: React.FC<{
  leagueIds: string[] | null;
  seasons: string[] | null;
}> = ({ leagueIds, seasons }) => {
  useScopeFromUrl(leagueIds, seasons);
  return null;
};

// Component to handle password recovery redirects
const PasswordRecoveryHandler: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if current session is a recovery session on mount
    const checkRecoverySession = async () => {
      try {
        const {
          data: { user },
          error: userFetchError,
        } = await supabase.auth.getUser();

        if (userFetchError) {
          console.error(
            "Error fetching user during recovery check:",
            userFetchError
          );
          return;
        }

        // Check if user has a recovery session by examining URL hash or query params
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1)
        );
        const searchParams = new URLSearchParams(window.location.search);
        const type = hashParams.get("type") || searchParams.get("type");

        // Also check if the session user's aud claim indicates recovery
        const isRecoverySession =
          type === "recovery" ||
          (user?.aud === "authenticated" &&
            (hashParams.has("access_token") ||
              searchParams.has("access_token")));

        if (
          isRecoverySession &&
          window.location.pathname !== "/reset-password"
        ) {
          console.log(
            "Recovery session detected on mount, redirecting to /reset-password"
          );
          navigate("/reset-password", { replace: true });
        }
      } catch (error) {
        console.error("Error checking recovery session:", error);
      }
    };

    checkRecoverySession();

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth event:", event, "Session user:", session?.user?.id);

        // When user clicks password recovery link, redirect them to reset password page
        if (event === "PASSWORD_RECOVERY") {
          console.log(
            "Password recovery detected, redirecting to /reset-password"
          );
          navigate("/reset-password", { replace: true });
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  return <>{children}</>;
};

export const AppRouter = () => {
  const { t } = useTranslation(["common", "auth"]);
  const { user, permissions } = useAuth();
  const { data: settings, isLoading: settingsLoading, isError: settingsError } = useAppSettings();
  const { data: leagues, isLoading: leaguesLoading, isError: leaguesError } = useLeagues();
  const {
    data: maintenance,
    isLoading: maintenanceLoading,
    refetch: refetchMaintenance,
    isFetching: maintenanceFetching,
  } = useMaintenanceStatus();
  const userIsSuperAdmin = checkIsSuperAdmin(user);
  const internalLeagues = useMemo(
    () =>
      (leagues || []).filter((league) => {
        return league.code !== EXTERNAL_LEAGUE_CODE;
      }),
    [leagues]
  );

  const visibleLeagues = useMemo(() => {
    // Super admins see all leagues
    const isSuperAdmin =
      user?.primaryRole === UserRole.SUPER_ADMIN ||
      user?.role === UserRole.SUPER_ADMIN ||
      user?.roles?.some((r) => r.role === UserRole.SUPER_ADMIN);

    console.log("[AppRouter] Calculating visibleLeagues:", {
      userId: user?.id,
      primaryRole: user?.primaryRole,
      isSuperAdmin,
      userLeagueIds: user?.leagueIds,
      totalInternalLeagues: internalLeagues.length,
      internalLeagueIds: internalLeagues.map((l) => l.id),
    });

    if (isSuperAdmin) {
      console.log(
        "[AppRouter] User is super admin, showing all internal leagues"
      );
      return internalLeagues;
    }

    // Regular users see only their assigned leagues
    if (!user?.leagueIds || user.leagueIds.length === 0) {
      // If no leagues assigned, show empty list (user needs league assignment)
      console.warn("[AppRouter] User has no leagueIds assigned!", {
        userId: user?.id,
        primaryRole: user?.primaryRole,
      });
      return [];
    }

    const allowed = new Set(user.leagueIds);
    const filtered = internalLeagues.filter((league) => allowed.has(league.id));
    console.log("[AppRouter] Filtered visible leagues:", {
      userLeagueIds: user.leagueIds,
      filtered: filtered.map((l) => ({ id: l.id, name: l.name })),
      filteredCount: filtered.length,
    });
    return filtered;
  }, [
    internalLeagues,
    user?.leagueIds,
    user?.primaryRole,
    user?.role,
    user?.roles,
  ]);

  const currentSeason = useLeagueSeasonStore((state) => state.currentSeason);
  const currentLeagueId = useLeagueSeasonStore(
    (state) => state.currentLeagueId
  );
  const setCurrentSeason = useLeagueSeasonStore(
    (state) => state.setCurrentSeason
  );
  const setCurrentLeagueId = useLeagueSeasonStore(
    (state) => state.setCurrentLeagueId
  );
  const ensureSeason = useLeagueSeasonStore((state) => state.ensureSeason);
  const ensureLeague = useLeagueSeasonStore((state) => state.ensureLeague);
  const { data: seasonRows } = useSeasons();
  // Removed obsolete settingsDirty state (managed inside SettingsContainer now)


  // Initialize season from localStorage if available; otherwise default to latest from settings
  useEffect(() => {
    if (!settings?.seasons?.length) return;
    const sortedSeasons = [...settings.seasons].sort((a, b) =>
      b.localeCompare(a)
    );
    const activeCode = (seasonRows ?? []).find(
      (s: any) => s.status === "active"
    )?.code;
    const preferred =
      activeCode && settings.seasons.includes(activeCode)
        ? activeCode
        : sortedSeasons[0];
    ensureSeason(sortedSeasons, preferred);
  }, [settings?.seasons, seasonRows, ensureSeason]);

  // Ligues sur lesquelles une session peut se trouver : les ligues actives
  // accessibles (toutes les accessibles si aucune n'est active). Une ligue
  // mémorisée absente ou inactive est remplacée par la première ligue active
  // qui a des groupes dans la saison courante (LIRF pour le super_admin), jamais
  // une ligue inactive comme la LFP.
  const usableLeagues = useMemo(() => getUsableLeagues(visibleLeagues), [visibleLeagues]);
  const currentLeagueUsable = usableLeagues.some((l) => l.id === currentLeagueId);
  const {
    data: leagueIdsWithGroups,
    isLoading: leagueGroupsLoading,
  } = useLeagueIdsWithGroups(currentSeason, !!leagues && !currentLeagueUsable);
  // Le choix par défaut attend la liste des ligues avec groupes, sinon une
  // ligue sans groupe (LNFA) serait retenue puis gardée.
  const leagueDefaultPending =
    !currentLeagueUsable &&
    usableLeagues.length > 0 &&
    ((!currentSeason && (settings?.seasons?.length ?? 0) > 0) || leagueGroupsLoading);

  useEffect(() => {
    if (!leagues) return;
    if (leagueDefaultPending) return;
    const availableLeagueIds = usableLeagues.map((league) => league.id);
    const preferred = pickDefaultLeagueId(visibleLeagues, currentLeagueId, leagueIdsWithGroups);
    ensureLeague(availableLeagueIds, preferred);
  }, [
    leagues,
    usableLeagues,
    visibleLeagues,
    currentLeagueId,
    leagueIdsWithGroups,
    leagueDefaultPending,
    ensureLeague,
  ]);

  const headerLeagues = useMemo(
    () => getHeaderLeagueOptions(visibleLeagues, currentLeagueId),
    [visibleLeagues, currentLeagueId],
  );

  // Portée `?league=&season=` des liens du tiroir d'engagements : validée contre
  // les mêmes listes que ensureSeason/ensureLeague, null tant qu'elles ne sont
  // pas chargées. Une valeur valide n'est donc jamais écrasée par ensure*.
  // Requête en erreur sans données : liste vide, les paramètres sont retirés
  // et ignorés au lieu d'attendre indéfiniment.
  const scopeLeagueIds = useMemo(
    () => (leagues ? usableLeagues.map((l) => l.id) : leaguesError ? [] : null),
    [leagues, usableLeagues, leaguesError],
  );
  const scopeSeasons = settings?.seasons ?? (settingsError ? [] : null);

  // Removed heavy data fetching from AppRouter - now done per-route:
  // - Teams, Matches, LeagueGroups -> RankingsView loads its own data
  // - AuditLogs -> AuditLogView loads its own data
  // This reduces initial load time from ~3s to <500ms!

  // Mutations
  const allRoles = Object.values(UserRole).map((roleName) => ({
    id: roleName,
    name: roleName,
  }));

  // Only wait for essential app-level data (settings, leagues are small/fast)
  // Maintenance status is awaited too so a paused app never flashes its content first.
  const isLoading = settingsLoading || leaguesLoading || maintenanceLoading;

  if (isLoading || leagueDefaultPending) {
    return <FullScreenLoader text={t("loading.loading_settings")} />;
  }

  // Mode maintenance : écran bloquant pour tout le monde sauf les super_admin.
  // Le serveur renvoie `exempt`; le contrôle client est un second filet.
  if (isMaintenanceBlocking(maintenance, "web", maintenance?.exempt || userIsSuperAdmin)) {
    return (
      <MaintenanceScreen
        status={maintenance!}
        onRetry={() => void refetchMaintenance()}
        isRetrying={maintenanceFetching}
      />
    );
  }
  // Bandeau super_admin dès qu'une maintenance est effective, quel que soit le scope visé.
  const showMaintenanceBanner = userIsSuperAdmin && !!maintenance?.effective;

  if (!currentSeason || !currentLeagueId) {
    console.error("[AppRouter] Missing league or season configuration:", {
      currentSeason,
      currentLeagueId,
      userId: user?.id,
      userLeagueIds: user?.leagueIds,
      primaryRole: user?.primaryRole,
      visibleLeaguesCount: visibleLeagues.length,
      visibleLeagues: visibleLeagues.map((l) => ({ id: l.id, name: l.name })),
      availableSeasons: settings?.seasons,
    });
    return (
      <div className="min-h-screen bg-white/5 flex items-center justify-center text-center text-white px-3">
        <div>
          <p className="text-lg font-semibold mb-2">
            {t("auth:errors.missing_config_title")}
          </p>
          <p className="text-sm text-white/60 mb-4">
            {t("auth:errors.missing_config_message")}
          </p>
          <button
            onClick={async () => {
              try {
                console.log(
                  "[AppRouter] Attempting logout from error screen..."
                );
                await supabase.auth.signOut();
                console.log("[AppRouter] Logout successful");
              } catch (e) {
                console.error("[AppRouter] Logout error:", e);
              }
            }}
            className="px-4 py-2 bg-rose-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {t("auth:logout.sign_out")}
          </button>
        </div>
      </div>
    );
  }

  // All assignment & mutation handlers moved to their respective containers.

  return (
    <BrowserRouter>
      <ScopeFromUrl leagueIds={scopeLeagueIds} seasons={scopeSeasons} />
      <PasswordRecoveryHandler>
        {showMaintenanceBanner && <MaintenanceBanner status={maintenance!} />}
        <Header seasons={settings?.seasons || []} leagues={headerLeagues} />
        <Suspense
          fallback={
            <RouteFallbackSkeleton />
          }
        >
          <Routes>
            <Route
              path="/planning"
              element={
                <ProtectedRoute
                  isAllowed={permissions.can("view", "dashboard")}
                  moduleKey="planning"
                >
                  <PlanningContainer />
                </ProtectedRoute>
              }
            >
              {/* Children pass isAllowed: the parent route already carries the
                  permission check, these gates only add the feature matrix. */}
              <Route index element={<PlanningIndexRedirect />} />
              <Route path="week" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="week"><WeekMatchesPage /></ProtectedRoute>
              } />
              <Route path="urgents" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="urgents"><UrgentsPage /></ProtectedRoute>
              } />
              <Route path="to-plan" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="to_plan"><ToPlanPage /></ProtectedRoute>
              } />
              <Route path="all" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="all_matches"><AllMatchesPage /></ProtectedRoute>
              } />
              <Route path="calendar" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="calendar_views"><CalendarPage /></ProtectedRoute>
              } />
              <Route path="calendar/week" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="calendar_views"><CalendarPage /></ProtectedRoute>
              } />
              <Route path="calendar/week/:iso" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="calendar_views"><CalendarPage /></ProtectedRoute>
              } />
              <Route path="calendar/:month" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="calendar_views"><CalendarPage /></ProtectedRoute>
              } />
              <Route path="faf-audit" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="faf_audit"><FafAuditPage /></ProtectedRoute>
              } />
              {/* `isAllowed` est un vrai littéral : la lecture est ouverte à
                  qui RLS laisse passer, la RPC est l'autorité. */}
              <Route path="engagements" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="engagements"><EngagementsPage /></ProtectedRoute>
              } />
              <Route path="focus/:groupId/:date" element={
                <ProtectedRoute isAllowed moduleKey="planning" featureKey="game_day_focus"><GameDayFocusPage /></ProtectedRoute>
              } />
            </Route>
            <Route
              path="/calendar"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "calendar")} moduleKey="calendar_generation">
                  <CalendarContainer />
                </ProtectedRoute>
              }
            >
              <Route index element={<CalendarLibraryPage />} />
              <Route
                path="config"
                element={
                  // isAllowed hérité du parent (déjà `view calendar`) : ce niveau
                  // n'ajoute que le gate de fonctionnalité, comme les enfants de
                  // /planning.
                  <ProtectedRoute isAllowed moduleKey="calendar_generation" featureKey="config">
                    <CalendarConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":groupId/draw"
                element={
                  <ProtectedRoute isAllowed={permissions.can("edit", "calendar")} moduleKey="calendar_generation" featureKey="draw">
                    <CalendarDrawRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":groupId/generate"
                element={
                  <ProtectedRoute isAllowed={permissions.can("edit", "calendar")} moduleKey="calendar_generation" featureKey="auto_generate">
                    <ModePickerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":groupId/generate/optimise"
                element={
                  <ProtectedRoute isAllowed={permissions.can("edit", "calendar")} moduleKey="calendar_generation" featureKey="auto_generate">
                    <OptimisePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":groupId/generate/manual"
                element={
                  <ProtectedRoute isAllowed={permissions.can("edit", "calendar")} moduleKey="calendar_generation" featureKey="manual_planner">
                    <ManualPage />
                  </ProtectedRoute>
                }
              />
              <Route path=":groupId/candidate/:candidateId" element={<CandidateDetailPage />} />
              <Route path=":groupId" element={<CandidatesHubPage />} />
            </Route>
            <Route
              path="/classements"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "rankings")} moduleKey="rankings">
                  <RankingsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/disciplinary/*"
              element={
                <ProtectedRoute
                  isAllowed={permissions.can("view", "disciplinary")}
                  moduleKey="disciplinary"
                >
                  <DisciplinaryContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/management/*"
              element={
                <ProtectedRoute
                  isAllowed={
                    permissions.can("view", "officials") ||
                    permissions.can("view", "disciplinary") ||
                    permissions.can("manage", "officials")
                  }
                  moduleKey="club_registry"
                >
                  <ManagementContainer />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/referee-proposals"
              element={
                <ProtectedRoute isAllowed={permissions.can("create", "assignment_proposal")} moduleKey="planning">
                  <RefereeProposalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/officials-management/*"
              element={
                <ProtectedRoute
                  isAllowed={
                    permissions.can("manage", "officials") ||
                    permissions.can("bulk_action", "officials") ||
                    permissions.can("view", "officials")
                  }
                  moduleKey="officials"
                >
                  <OfficialsManagementContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clubs/*"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "clubs")} moduleKey="club_registry" featureKey="clubs">
                  <ClubsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stadiums/*"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "stadium")} moduleKey="stadiums_audits" featureKey="stadiums">
                  <StadiumsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/missions"
              element={
                <ProtectedRoute
                  isAllowed={
                    permissions.can("view", "stadium_audit") ||
                    permissions.can("view", "missions")
                  }
                  moduleKey="stadiums_audits"
                  featureKey="audit_planning"
                >
                  <StadiumAuditPlannerContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finances"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "finances")} moduleKey="payments_treasury" featureKey="finances_dashboard">
                  <FinancesContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounting/*"
              element={
                <ProtectedRoute
                  isAllowed={permissions.can("view", "accounting")}
                  moduleKey="accounting"
                >
                  <AccountingContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/virements/*"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "finances")} moduleKey="payments_treasury" featureKey="virements">
                  <VirementsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/etats"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "finances")} moduleKey="payments_treasury" featureKey="etats">
                  <EtatsContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit"
              element={
                <ProtectedRoute
                  isAllowed={permissions.can("view", "audit_log")}
                >
                  <AuditLogContainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/*"
              element={
                <ProtectedRoute isAllowed={canOpenSettings(permissions)}>
                  <SettingsContainer />
                </ProtectedRoute>
              }
            />
            {/* News Management Route */}
            <Route
              path="/news"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "news")} moduleKey="news">
                  <NewsContainer />
                </ProtectedRoute>
              }
            />
            {/* Media Accreditation Route */}
            <Route
              path="/media"
              element={
                <ProtectedRoute isAllowed={permissions.can("view", "media")} moduleKey="media_accreditation">
                  <MediaContainer />
                </ProtectedRoute>
              }
            />
            {/* Gate scan mode. Guard kept in lockstep with the /media/scan
                candidate of lib/auth/landingRoute.ts (security_gate lands here). */}
            <Route
              path="/media/scan"
              element={
                <ProtectedRoute isAllowed={permissions.can("scan", "media_access_log")} moduleKey="media_accreditation">
                  <MediaScanModePage />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<DefaultRedirect />} />

            {/* Error pages */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="/500" element={<ServerErrorPage />} />
            <Route path="/network-error" element={<NetworkErrorPage />} />

            {/* Catch all - 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </PasswordRecoveryHandler>
    </BrowserRouter>
  );
};
